// components/FeaturedCaseStudy.tsx
"use client";

import React, { useCallback, useEffect, useState } from "react";
import {
  Box,
  Container,
  Grid,
  Typography,
  Card,
  CardContent,
  Button,
  Chip,
  useTheme,
  alpha,
  Skeleton,
  Alert,
} from "@mui/material";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import TrendingUpIcon from "@mui/icons-material/TrendingUp";
import ScheduleIcon from "@mui/icons-material/Schedule";
import ShowChartIcon from "@mui/icons-material/ShowChart";
import { CaseStudy } from "@/types/case-study";
import {
  collection,
  getDocs,
  limit,
  orderBy,
  query,
  where,
} from "firebase/firestore";
import { db } from "@/lib/firebase";
import Link from "next/link";
import { shades } from "@/styles/theme";
import { JSX } from "@emotion/react/jsx-runtime";

// Define a proper type for metrics
type MetricType = {
  name?: string;
  before?: string;
  after?: string;
  improvement?: string;
  icon?: JSX.Element;
  value?: string;
  label?: string;
};

const getMetricIcon = (metric: MetricType, index: number): JSX.Element => {
  if (metric.icon) return metric.icon;
  const icons = [
    <TrendingUpIcon key="trending" />,
    <ScheduleIcon key="schedule" />,
    <ShowChartIcon key="chart" />,
  ];
  return icons[index] || <TrendingUpIcon key="default-trending" />;
};

const getMetricValue = (metric: MetricType): string => {
  if (metric.value) return metric.value;
  if (metric.improvement) return metric.improvement;
  return "N/A";
};

const getMetricLabel = (metric: MetricType): string => {
  if (metric.label) return metric.label;
  if (metric.name) return metric.name;
  return "Improvement";
};

export default function FeaturedCaseStudy() {
  const theme = useTheme();
  const [featuredCaseStudy, setFeaturedCaseStudy] = useState<CaseStudy | null>(
    null
  );
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchFeaturedCaseStudy = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);

      const q = query(
        collection(db, "caseStudies"),
        where("isFeatured", "==", true),
        where("isActive", "==", true),
        orderBy("createdAt", "desc"),
        limit(1)
      );

      const querySnapshot = await getDocs(q);

      if (querySnapshot.empty) {
        setFeaturedCaseStudy(null);
        return;
      }

      const caseStudyData = querySnapshot.docs[0].data();
      const caseStudy: CaseStudy = {
        id: querySnapshot.docs[0].id,
        slug: querySnapshot.docs[0].id,
        title: caseStudyData.title || "Untitled Case Study",
        client: caseStudyData.client || "",
        excerpt: caseStudyData.excerpt || "",
        industry: caseStudyData.industry || "",
        services: caseStudyData.services || [],
        duration: caseStudyData.duration || "",
        projectUrl: caseStudyData.projectUrl || "",
        featuredImage: caseStudyData.featuredImage || "",
        isFeatured: caseStudyData.isFeatured || false,
        challenge: caseStudyData.challenge || "",
        githubUrl: caseStudyData.githubUrl || "",
        technologies: caseStudyData.technologies || [],
        solution: caseStudyData.solution || {
          overview: "",
          steps: [],
        },
        results: caseStudyData.results || {
          metrics: [],
          additionalResults: [],
        },
        keyLearnings: caseStudyData.keyLearnings || [],
        testimonial: caseStudyData.testimonial || {
          quote: "",
          author: "",
          position: "",
          company: "",
        },
        gallery: caseStudyData.gallery || [],
        isActive: caseStudyData.isActive ?? true,
        createdAt: caseStudyData.createdAt || new Date().toISOString(),
        updatedAt: caseStudyData.updatedAt || new Date().toISOString(),
      };

      setFeaturedCaseStudy(caseStudy);
    } catch (error) {
      console.error("Fetch error:", error);
      setError("Failed to load featured case study. Please try again.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchFeaturedCaseStudy();
  }, [fetchFeaturedCaseStudy]);

  // Don't render if no featured case study
  if (!loading && !featuredCaseStudy) {
    return null;
  }

  // Use actual metrics from case study or fallback to defaults
  const displayMetrics = featuredCaseStudy?.results?.metrics?.slice(0, 3) || [
    {
      icon: <TrendingUpIcon />,
      value: "45%",
      label: "Increase in Online Sales",
    },
    {
      icon: <ScheduleIcon />,
      value: "60%",
      label: "Reduction in Process Time",
    },
    {
      icon: <ShowChartIcon />,
      value: "3.2x",
      label: "ROI in First Year",
    },
  ];

  // Use actual solution highlights or fallback to defaults
  const solutionHighlights = featuredCaseStudy?.solution?.steps?.[0]
    ?.details || [
    "Custom e-commerce platform with React/Next.js",
    "AI-powered product recommendations",
    "Automated inventory synchronization",
    "Multi-payment gateway integration",
    "Real-time analytics dashboard",
  ];

  return (
    <Box
      sx={{
        py: { xs: 6, md: 10 },
        background: `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.02)} 0%, ${alpha(theme.palette.secondary.main, 0.02)} 100%)`,
      }}
    >
      <Container maxWidth="lg">
        <Box sx={{ textAlign: "center", mb: { xs: 4, md: 8 } }}>
          <Typography
            variant="h2"
            gutterBottom
            sx={{
              fontWeight: 700,
              background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.secondary.main} 100%)`,
              backgroundClip: "text",
              WebkitBackgroundClip: "text",
              color: "transparent",
              fontSize: { xs: "2.5rem", md: "3rem" },
            }}
          >
            Featured Case Study
          </Typography>
          <Typography
            variant="h6"
            component={"p"}
            color="text.secondary"
            sx={{
              maxWidth: 600,
              mx: "auto",
              fontSize: { xs: "1.1rem", md: "1.25rem" },
              lineHeight: 1.6,
            }}
          >
            Discover how we helped businesses achieve remarkable results with
            our solutions
          </Typography>
        </Box>

        {/* Error State */}
        {error && (
          <Alert
            severity="error"
            sx={{ mb: 4 }}
            action={
              <Button
                color="inherit"
                size="small"
                onClick={fetchFeaturedCaseStudy}
              >
                Retry
              </Button>
            }
          >
            {error}
          </Alert>
        )}

        {/* Loading State */}
        {loading ? (
          <FeaturedCaseStudySkeleton />
        ) : featuredCaseStudy ? (
          <Card
            sx={{
              background: "white",
              border: `1px solid ${alpha(theme.palette.primary.main, 0.1)}`,
              boxShadow: `0 20px 40px ${alpha(theme.palette.primary.main, 0.1)}`,
              overflow: "hidden",
            }}
          >
            <Grid container>
              {/* Image Section */}
              <Grid size={{ xs: 12, md: 6 }}>
                <Box
                  sx={{
                    height: "100%",
                    minHeight: 400,
                    background: featuredCaseStudy.featuredImage
                      ? `url(${featuredCaseStudy.featuredImage}) center/cover`
                      : `linear-gradient(135deg, ${shades.primary[400]} 0%, ${shades.secondary[400]} 100%)`,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    color: "white",
                    position: "relative",
                    "&::before": featuredCaseStudy.featuredImage
                      ? {
                          content: '""',
                          position: "absolute",
                          top: 0,
                          left: 0,
                          right: 0,
                          bottom: 0,
                          background:
                            "linear-gradient(135deg, rgba(0,0,0,0.6) 0%, rgba(0,0,0,0.3) 100%)",
                        }
                      : {},
                  }}
                >
                  <Box
                    sx={{
                      textAlign: "center",
                      p: 4,
                      position: "relative",
                      zIndex: 1,
                    }}
                  >
                    <Typography
                      variant="h4"
                      component="h3"
                      gutterBottom
                      sx={{ fontWeight: 600 }}
                    >
                      {featuredCaseStudy.client}
                    </Typography>
                    <Typography
                      variant="h6"
                      component={"p"}
                      sx={{ opacity: 0.9, mb: 3 }}
                    >
                      {featuredCaseStudy.industry}
                    </Typography>
                    <Chip
                      label={
                        featuredCaseStudy.services?.slice(0, 2).join(" + ") ||
                        "Web Development"
                      }
                      sx={{
                        background: "rgba(255,255,255,0.2)",
                        color: "white",
                        fontWeight: 600,
                        border: "1px solid rgba(255,255,255,0.3)",
                      }}
                    />
                  </Box>
                </Box>
              </Grid>

              {/* Content Section */}
              <Grid size={{ xs: 12, md: 6 }}>
                <CardContent
                  sx={{
                    p: 5,
                    height: "100%",
                    display: "flex",
                    flexDirection: "column",
                  }}
                >
                  <Typography
                    variant="h5"
                    component="p"
                    gutterBottom
                    sx={{ fontWeight: 600 }}
                  >
                    {featuredCaseStudy.title}
                  </Typography>

                  <Typography
                    variant="body1"
                    color={`${shades.secondary[600]}`}
                    paragraph
                    sx={{ lineHeight: 1.7 }}
                  >
                    {featuredCaseStudy.excerpt}
                  </Typography>

                  <Typography
                    variant="h6"
                    component={"p"}
                    gutterBottom
                    sx={{ fontWeight: 600, mt: 3 }}
                  >
                    Key Achievements:
                  </Typography>

                  {/* Metrics */}
                  <Box sx={{ mb: 4 }}>
                    <Grid container spacing={3}>
                      {displayMetrics.map((metric, index) => (
                        <Grid size={{ xs: 12, sm: 4 }} key={index}>
                          <Box sx={{ textAlign: "center" }}>
                            <Box
                              sx={{
                                display: "inline-flex",
                                alignItems: "center",
                                justifyContent: "center",
                                width: 60,
                                height: 60,
                                borderRadius: "50%",
                                backgroundColor: alpha(
                                  theme.palette.primary.main,
                                  0.1
                                ),
                                color: theme.palette.primary.main,
                                mb: 1,
                              }}
                            >
                              {getMetricIcon(metric, index)}
                            </Box>
                            <Typography
                              variant="h4"
                              component="div"
                              fontWeight={700}
                              color="primary"
                            >
                              {getMetricValue(metric)}
                            </Typography>
                            <Typography
                              variant="body2"
                              color="text.secondary"
                              fontSize={18}
                              sx={{
                                fontVariant: "small-caps",
                                background: alpha(
                                  theme.palette.primary.main,
                                  0.08
                                ),
                                display: "inline-block",
                                px: 1,
                                py: 0.5,
                                borderRadius: 1,
                                mt: 0.5,
                              }}
                            >
                              {getMetricLabel(metric)}
                            </Typography>
                          </Box>
                        </Grid>
                      ))}
                    </Grid>
                  </Box>

                  {/* Solution Highlights */}
                  <Box sx={{ mb: 4 }}>
                    <Typography
                      variant="h6"
                      component={"p"}
                      gutterBottom
                      sx={{ fontWeight: 600 }}
                    >
                      Solution Highlights:
                    </Typography>
                    {solutionHighlights.slice(0, 5).map((highlight, index) => (
                      <Typography
                        key={index}
                        variant="body2"
                        fontSize={{ xs: 16, md: 19 }}
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          mb: 1,
                          background: alpha(shades.secondary[700], 0.08),
                          px: 1,
                          py: 0.5,
                          borderRadius: 1,
                          "&:before": {
                            content: '"✓"',
                            color: theme.palette.success.main,
                            fontWeight: "bold",
                            mr: 1,
                            background: alpha(theme.palette.primary.main, 0.5),
                            borderRadius: "50%",
                            width: 20,
                            height: 20,
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                          },
                        }}
                      >
                        {highlight}
                      </Typography>
                    ))}
                  </Box>

                  <Box
                    sx={{
                      mt: "auto",
                      display: "flex",
                      gap: 2,
                      flexWrap: "wrap",
                    }}
                  >
                    <Button
                      component={Link}
                      href="/case-studies"
                      variant="contained"
                      endIcon={<ArrowForwardIcon />}
                      sx={{
                        background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.secondary.main} 100%)`,
                        "&:hover": {
                          transform: "translateY(-2px)",
                          boxShadow: `0 8px 25px ${alpha(theme.palette.primary.main, 0.3)}`,
                        },
                        transition: "all 0.3s ease",
                      }}
                    >
                      View Full Case Study
                    </Button>
                    {/* <Button
                      component={Link}
                      href="/case-studies"
                      variant="outlined"
                      endIcon={<ArrowForwardIcon />}
                    >
                      View All Projects
                    </Button> */}
                  </Box>
                </CardContent>
              </Grid>
            </Grid>
          </Card>
        ) : null}
      </Container>
    </Box>
  );
}

// Skeleton Loading Component
function FeaturedCaseStudySkeleton() {
  return (
    <Card
      sx={{
        background: "white",
        border: "1px solid rgba(0,0,0,0.1)",
        boxShadow: "0 20px 40px rgba(0,0,0,0.1)",
        overflow: "hidden",
      }}
    >
      <Grid container>
        {/* Image Section Skeleton */}
        <Grid size={{ xs: 12, md: 6 }}>
          <Skeleton
            variant="rectangular"
            height={400}
            sx={{
              bgcolor: "grey.200",
              borderRadius: 0,
            }}
          />
        </Grid>

        {/* Content Section Skeleton */}
        <Grid size={{ xs: 12, md: 6 }}>
          <CardContent sx={{ p: 5, height: "100%" }}>
            <Skeleton variant="text" height={40} sx={{ mb: 2 }} />
            <Skeleton variant="text" height={20} sx={{ mb: 1 }} />
            <Skeleton variant="text" height={20} sx={{ mb: 1 }} />
            <Skeleton variant="text" height={20} sx={{ mb: 3 }} />

            <Skeleton variant="text" height={30} sx={{ mb: 2 }} />

            {/* Metrics Skeleton */}
            <Grid container spacing={3} sx={{ mb: 4 }}>
              {[1, 2, 3].map((item) => (
                <Grid size={{ xs: 12, sm: 4 }} key={item}>
                  <Box sx={{ textAlign: "center" }}>
                    <Skeleton
                      variant="circular"
                      width={60}
                      height={60}
                      sx={{ mb: 1, mx: "auto" }}
                    />
                    <Skeleton variant="text" height={30} sx={{ mb: 1 }} />
                    <Skeleton
                      variant="text"
                      height={20}
                      width="80%"
                      sx={{ mx: "auto" }}
                    />
                  </Box>
                </Grid>
              ))}
            </Grid>

            {/* Solution Highlights Skeleton */}
            <Skeleton variant="text" height={30} sx={{ mb: 2 }} />
            {[1, 2, 3, 4, 5].map((item) => (
              <Skeleton key={item} variant="text" height={20} sx={{ mb: 1 }} />
            ))}

            {/* Buttons Skeleton */}
            <Box sx={{ mt: 4, display: "flex", gap: 2 }}>
              <Skeleton variant="rectangular" height={40} width={200} />
              <Skeleton variant="rectangular" height={40} width={160} />
            </Box>
          </CardContent>
        </Grid>
      </Grid>
    </Card>
  );
}
